import { SET_DESTINATIONS_VIEW } from "Containers/Destinations";
import {
  SetDestinationsViewPayload,
  setDestinationsViewTrigger,
} from "Containers/Destinations/actions";
import { SET_CURRENT_STOP } from "Containers/Stops";
import {
  CurrentStopActionTypes,
  stopChangeTrigger,
} from "Containers/Stops/currentStop.Actions";
import { ActionHandlers } from "../types";
import { getDateTimeStringInEst, getTimeSpentInSeconds } from "../utils";

export const DestinationEvents = {
  DESTINATION_LIST_CLICKED: "Destination_List_Clicked",
  DESTINATION_LIST_OPENED: "Destination_List_Opened",
  DESTINATION_LIST_CLOSED: "Destination_List_Closed",
  TOUR_STOP_VISIT: "Tour_Stop_Visit",
};

const findStopAndStopNumber = (stops, stopId) => {
  if (stopId === -1) {
    return [undefined, undefined];
  }

  let stop;
  let stopIndex;
  // find stop and stopIndex
  stops.some((s, index) => {
    if (s.id === stopId) {
      stop = s;
      stopIndex = index;
      return true;
    }
    return false;
  });

  // stopNumber starts at 1 (instead of 0 like stopIndex)
  const stopNumber = stopIndex !== undefined ? stopIndex + 1 : undefined;
  return [stop, stopNumber];
};

let destinationListOpenedTimestamp: Date | undefined;

export const destinationActionHandlers: ActionHandlers = {
  [SET_CURRENT_STOP]: (
    reduxState,
    payload: CurrentStopActionTypes["payload"]
  ) => {
    const { currentStop: currentStopId, trigger } = payload;
    if (currentStopId === -1) {
      // do not track if currentStopId is -1 (i.e. no stop selected)
      return null;
    }

    const {
      stops,
      currentStop: { currentStop: previousStopId, stopsVisitedCount },
    } = reduxState;

    const [currentStop, currentStopNumber] = findStopAndStopNumber(
      stops,
      currentStopId
    );
    const [previousStop, previousStopNumber] = findStopAndStopNumber(
      stops,
      previousStopId
    );
    const tourStopsCount = stops?.length ?? 0;

    let mapPlaceType;
    if (currentStop?.isPointOfInterest === undefined) {
      mapPlaceType = "None";
    } else {
      mapPlaceType = currentStop.isPointOfInterest ? "POI" : "Destination";
    }

    // Tour_Stop_Visit event
    const stopVisitTrackData = {
      tour_stop_name_switched_to: currentStop?.title,
      tour_stop_number_switched_to: currentStopNumber,
      tour_stop_name_switched_from: previousStop?.title,
      tour_stop_number_switched_from: previousStopNumber,
      method:
        trigger === stopChangeTrigger.DESTINATION_LIST
          ? "Destination List"
          : "Other",
      count_of_tour_stops_visited: stopsVisitedCount,
      count_of_tour_stops_available: tourStopsCount,
      active_experience_product: "iMap",
      map_place_type: mapPlaceType,
    };

    if (trigger === stopChangeTrigger.DESTINATION_LIST) {
      // Destination_List_Clicked event
      const destinationListClickedTrackData = {
        tour_stop_name_switched_to: currentStop?.title,
        tour_stop_number_switched_to: currentStopNumber,
        tour_stop_name_switched_from: previousStop?.title,
        tour_stop_number_switched_from: previousStopNumber,
        count_of_tour_stops_available: tourStopsCount,
        active_experience_product: "iMap",
      };

      return [
        [
          DestinationEvents.DESTINATION_LIST_CLICKED,
          destinationListClickedTrackData,
          "gainsight",
        ],
        [DestinationEvents.TOUR_STOP_VISIT, stopVisitTrackData, "gainsight"],
      ];
    }

    return [DestinationEvents.TOUR_STOP_VISIT, stopVisitTrackData, "gainsight"];
  },

  [SET_DESTINATIONS_VIEW]: (
    reduxState,
    payload: SetDestinationsViewPayload
  ) => {
    const { show: showDestinationList, trigger } = payload;
    const { stops } = reduxState;
    const tourStopsCount = stops?.length ?? 0;

    if (showDestinationList) {
      // "Destination_List_Opened" event
      const now = new Date();
      destinationListOpenedTimestamp = now;

      const destListOpenedTrackData = {
        method:
          trigger === setDestinationsViewTrigger.BUTTON_CLICK
            ? "clicked menu title/button"
            : "other",
        count_of_tour_stops_available: tourStopsCount,
        active_experience_product: "iMap",
        destination_list_opened_timestamp: getDateTimeStringInEst(now),
      };

      return [
        DestinationEvents.DESTINATION_LIST_OPENED,
        destListOpenedTrackData,
        "gainsight",
      ];
    }

    // "Destination_List_Closed" event
    const now = new Date();

    const destListClosedTrackData = {
      method:
        trigger === setDestinationsViewTrigger.DESTINATION_CLICK
          ? "destination clicked"
          : "other",
      count_of_tour_stops_available: tourStopsCount,
      active_experience_product: "iMap",
      destination_list_closed_timestamp: getDateTimeStringInEst(now),
      destination_list_closed_timespent: getTimeSpentInSeconds(
        destinationListOpenedTimestamp,
        now
      ),
    };

    return [
      DestinationEvents.DESTINATION_LIST_CLOSED,
      destListClosedTrackData,
      "gainsight",
    ];
  },
};
